import { type FormEvent, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import type { AuditEvent, Punch, PunchMethod, Site, Visit, VisitEditBody } from "../../shared/types";
import { Dialog, ErrorBanner, Field, FlagBadges, Loading, ResultBadge, StatusBadge } from "../components/ui";
import { ApiError, patch, post } from "../lib/api";
import {
  fmtDate,
  fmtDateTime,
  fmtDistance,
  fmtDuration,
  fmtTime,
  fromLocalInput,
  toLocalInput,
} from "../lib/format";
import { googleMapsLink } from "../lib/maps";
import { useApi } from "../lib/useApi";
import { AuditTable, LoadState, PageHeader, ReasonDialog, toApiError, useSites } from "./common";
import { isPayable, isRejected, notPayableReason, outRejected } from "./payable";

type Part = "in" | "out";

const METHOD_LABEL: Record<PunchMethod, string> = {
  qr: "QR scan",
  no_qr: "No scan (couldn't scan the sticker)",
  admin: "Entered by an admin",
};

const LOCATION_LABEL = {
  inside: "Inside the geofence",
  outside: "Outside the geofence",
  unavailable: "Unavailable",
};

function PunchCard({
  title,
  punch,
  gpsTrimmed,
  onApprove,
  onReject,
  busy,
}: {
  title: string;
  punch: Punch | null;
  gpsTrimmed: boolean;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  if (!punch) {
    return (
      <section className="card">
        <h2>{title}</h2>
        <p className="muted">Not clocked out yet.</p>
      </section>
    );
  }
  const hasCoords = punch.lat != null && punch.lng != null;
  return (
    <section className="card stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>{title}</h2>
        <ResultBadge result={punch.result} />
      </div>
      <dl className="adm-dl">
        <dt>Time</dt>
        <dd>{fmtDateTime(punch.at)}</dd>
        {punch.offline && (
          <>
            <dt>Received</dt>
            <dd>{fmtDateTime(punch.receivedAt)} (sent later, phone was offline)</dd>
          </>
        )}
        <dt>Method</dt>
        <dd>{METHOD_LABEL[punch.method] ?? punch.method}</dd>
        <dt>Flags</dt>
        <dd>
          {punch.flags.length ? <FlagBadges flags={punch.flags} /> : <span className="muted">None</span>}
        </dd>
        <dt>Location</dt>
        <dd>
          {punch.location ? LOCATION_LABEL[punch.location] : <span className="muted">Not recorded</span>}
        </dd>
        <dt>Distance</dt>
        <dd>{punch.distanceM != null ? `${fmtDistance(punch.distanceM)} from the pin` : "—"}</dd>
        <dt>GPS accuracy</dt>
        <dd>{punch.accuracyM != null ? `± ${fmtDistance(punch.accuracyM)}` : "—"}</dd>
        <dt>Map</dt>
        <dd>
          {hasCoords ? (
            <a href={googleMapsLink(punch.lat!, punch.lng!)} target="_blank" rel="noreferrer">
              View on map
            </a>
          ) : gpsTrimmed ? (
            <span className="muted">GPS removed after retention period</span>
          ) : (
            <span className="muted">No GPS position recorded</span>
          )}
        </dd>
        <dt>Cleaner's note</dt>
        <dd>{punch.note ? <span style={{ whiteSpace: "pre-wrap" }}>{punch.note}</span> : "—"}</dd>
      </dl>
      {punch.result === "pending" && (
        <div className="banner info">
          <div className="row between">
            <span>This {title.toLowerCase()} is waiting for your approval.</span>
            <span className="row">
              <button className="btn primary sm" onClick={onApprove} disabled={busy}>
                Approve
              </button>
              <button className="btn sm" onClick={onReject} disabled={busy}>
                Reject
              </button>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/** The 409 a PATCH gets for a visit that is already in an active export batch. */
function exportedBatches(err: unknown): number[] | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.code !== "already_exported") return null;
  const b = err.body.batches;
  return Array.isArray(b) ? b.filter((n): n is number => typeof n === "number") : [];
}

function exportedWarning(batches: number[]): string {
  const list = batches.map((b) => `#${b}`).join(", ");
  const one = batches.length === 1;
  return `This visit is in export batch ${list || "(unknown)"}. Your change won't update the file already sent to payroll — void and re-export ${one ? "that batch" : "those batches"} if needed.`;
}

function EditVisitDialog({
  visit,
  sites,
  onClose,
  onSaved,
}: {
  visit: Visit;
  sites: Site[];
  onClose: () => void;
  onSaved: (v: Visit) => void;
}) {
  const nowInput = toLocalInput(Date.now());
  // No accepted clock-out yet: either none was recorded, or the one recorded was rejected.
  // Any time set here (even the rejected one, kept as it was) closes the visit.
  // (A rejected visit stays rejected whatever is set, so it is edited like any other.)
  const needsOut =
    visit.status === "missing_clock_out" || (visit.status !== "rejected" && outRejected(visit));
  const rejectedOut = needsOut && outRejected(visit) ? visit.out : null;
  const savedIn = toLocalInput(visit.in.at);
  const savedOut = visit.out && !needsOut ? toLocalInput(visit.out.at) : "";
  const [inAt, setInAt] = useState(savedIn);
  // A rejected time isn't pre-filled: the admin types the confirmed finish time, or picks
  // "Use that time" below, so editing only the note never closes the visit by accident.
  const [outAt, setOutAt] = useState(savedOut);
  const [breakMinutes, setBreak] = useState(String(visit.breakMinutes));
  const [siteId, setSiteId] = useState(visit.siteId);
  const [adminNote, setAdminNote] = useState(visit.adminNote ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exported, setExported] = useState<{ batches: number[]; body: VisitEditBody } | null>(null);

  async function save(body: VisitEditBody) {
    setBusy(true);
    setError(null);
    try {
      onSaved(await patch<Visit>(`/admin/visits/${visit.id}`, body));
    } catch (err) {
      const batches = body.allowExported ? null : exportedBatches(err);
      if (batches) setExported({ batches, body });
      else setError(toApiError(err).message);
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError("Please give a reason for the change.");
    if (!inAt) return setError("Clock-in time is required.");
    // Only send what changed so the audit log shows exactly what was edited. An unchanged
    // input keeps the stored instant (re-deriving it could pick the other 01:xx when the
    // clocks go back).
    const body: VisitEditBody = { reason: reason.trim() };
    const inMs = inAt === savedIn ? visit.in.at : fromLocalInput(inAt);
    if (inAt !== savedIn) body.inAt = inMs;
    if (outAt) {
      const outMs =
        rejectedOut && outAt === toLocalInput(rejectedOut.at)
          ? rejectedOut.at
          : visit.out && outAt === savedOut
            ? visit.out.at
            : fromLocalInput(outAt);
      if (outMs <= inMs) return setError("Clock-out must be after clock-in.");
      // With no accepted clock-out, always send it: that is what approves it and closes the visit.
      if (needsOut || outAt !== savedOut) body.outAt = outMs;
    } else if (visit.out && !needsOut) {
      return setError("The clock-out time can be changed but not removed.");
    }
    const br = Number(breakMinutes) || 0;
    if (br !== visit.breakMinutes) body.breakMinutes = br;
    if (siteId !== visit.siteId) body.siteId = siteId;
    if (adminNote.trim() !== (visit.adminNote ?? "")) body.adminNote = adminNote.trim();
    if (Object.keys(body).length === 1) return setError("Nothing has changed.");
    void save(body);
  }

  const outHint = needsOut
    ? rejectedOut
      ? `The clock-out at ${fmtTime(rejectedOut.at)} was rejected. Setting a time, even that one, closes the visit.`
      : "Setting a time closes the visit."
    : visit.out
      ? "Can be changed but not removed."
      : "Setting a time closes the visit.";

  return (
    <Dialog title="Edit visit" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p className="muted small">
          {visit.userName}. Times are UK time. Every change is kept in the audit log with your reason.
        </p>
        {/* Locked while the "already exported" question is open, so what is confirmed is what is sent. */}
        <fieldset className="adm-fieldset stack" disabled={!!exported || busy}>
          <div className="grid cols-2">
            <Field label="Clock-in">
              <input
                type="datetime-local"
                value={inAt}
                max={nowInput}
                onChange={(e) => setInAt(e.target.value)}
                required
              />
            </Field>
            <Field label="Clock-out" hint={outHint}>
              <input
                type="datetime-local"
                value={outAt}
                min={inAt}
                max={nowInput}
                onChange={(e) => setOutAt(e.target.value)}
                required={!!visit.out && !needsOut}
              />
            </Field>
          </div>
          {rejectedOut && outAt !== toLocalInput(rejectedOut.at) && (
            <div className="row">
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => setOutAt(toLocalInput(rejectedOut.at))}
              >
                Use {fmtTime(rejectedOut.at)} as the clock-out (the rejected time)
              </button>
            </div>
          )}
          <div className="grid cols-2">
            <Field label="Break (minutes)">
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={600}
                value={breakMinutes}
                onChange={(e) => setBreak(e.target.value)}
              />
            </Field>
            <Field label="Site">
              <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.active ? "" : " (inactive)"}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Admin note" hint="Visible to admins only.">
            <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} maxLength={1000} />
          </Field>
          <Field label="Reason for the change">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Forgot to clock out, confirmed finish time by text"
              required
            />
          </Field>
        </fieldset>
        {error && <div className="banner danger">{error}</div>}
        {exported ? (
          <div className="banner warn stack" role="alert">
            <p style={{ margin: 0 }}>{exportedWarning(exported.batches)}</p>
            <div className="row">
              <button
                type="button"
                className="btn primary"
                disabled={busy}
                onClick={() => void save({ ...exported.body, allowExported: true })}
              >
                {busy ? "Saving…" : "Save anyway"}
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => setExported(null)}>
                Back
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        )}
      </form>
    </Dialog>
  );
}

export function VisitPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const visit = useApi<Visit>(`/admin/visits/${encodeURIComponent(id)}`);
  const audit = useApi<AuditEvent[]>(`/admin/audit?entity=visit&entityId=${encodeURIComponent(id)}`);
  const sites = useSites();
  const [rejecting, setRejecting] = useState<Part | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const editing = params.get("edit") === "1";
  const setEditing = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set("edit", "1");
    else next.delete("edit");
    setParams(next, { replace: true });
  };

  const back = { to: "/admin/timesheets", label: "Timesheets" };
  if (!visit.data) {
    return (
      <div className="page">
        <PageHeader title="Visit" back={back} />
        {visit.error ? <ErrorBanner error={visit.error} onRetry={visit.reload} /> : <Loading />}
      </div>
    );
  }
  const v = visit.data;

  const updated = (nv: Visit, msg: string) => {
    visit.setData(nv);
    setMessage(msg);
    void audit.reload();
  };

  async function approve(part: Part) {
    setBusy(true);
    setActionError(null);
    try {
      updated(await post<Visit>(`/admin/visits/${v.id}/approve`, { part }), "Approved.");
    } catch (e) {
      setActionError(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title={
          <>
            {v.userName} · {v.siteName}
          </>
        }
        sub={
          <span className="row" style={{ gap: 6 }}>
            {fmtDate(v.in.at)} <StatusBadge status={v.status} />
          </span>
        }
        back={back}
        actions={
          <button className="btn" onClick={() => setEditing(true)} disabled={!sites.data}>
            Edit
          </button>
        }
      />
      {message && <div className="banner ok">{message}</div>}
      {actionError && <div className="banner danger">{actionError}</div>}
      {v.status === "missing_clock_out" && (
        <div className="banner warn">
          <div className="row between">
            <span>
              {outRejected(v)
                ? `The clock-out at ${fmtTime(v.out!.at)} was rejected. Set the finish time (or confirm that one) so the hours count.`
                : "This visit has no clock-out. Set the finish time so the hours count."}
            </span>
            <button className="btn sm" onClick={() => setEditing(true)} disabled={!sites.data}>
              Set clock-out time
            </button>
          </div>
        </div>
      )}

      <section className="card adm-section">
        <dl className="adm-dl">
          <dt>Person</dt>
          <dd>
            <Link to={`/admin/people/${v.userId}`}>{v.userName}</Link>
          </dd>
          <dt>Site</dt>
          <dd>
            <Link to={`/admin/sites/${v.siteId}`}>{v.siteName}</Link>
          </dd>
          <dt>Worked</dt>
          <dd>
            {isRejected(v) ? (
              "Doesn't count (rejected)"
            ) : isPayable(v) ? (
              fmtDuration(v.minutes)
            ) : (
              <>
                {outRejected(v) ? "—" : fmtDuration(v.minutes)}{" "}
                <span className="muted">(not payable yet: {notPayableReason(v)?.toLowerCase()})</span>
              </>
            )}
          </dd>
          <dt>Break</dt>
          <dd>{v.breakMinutes ? `${v.breakMinutes} minutes` : "None"}</dd>
          <dt>Admin note</dt>
          <dd>{v.adminNote ? <span style={{ whiteSpace: "pre-wrap" }}>{v.adminNote}</span> : "—"}</dd>
        </dl>
      </section>

      <div className="grid cols-2 adm-section">
        <PunchCard
          title="Clock-in"
          punch={v.in}
          gpsTrimmed={v.gpsTrimmed}
          onApprove={() => void approve("in")}
          onReject={() => setRejecting("in")}
          busy={busy}
        />
        <PunchCard
          title="Clock-out"
          punch={v.out}
          gpsTrimmed={v.gpsTrimmed}
          onApprove={() => void approve("out")}
          onReject={() => setRejecting("out")}
          busy={busy}
        />
      </div>

      <section className="adm-section">
        <h2>History</h2>
        <LoadState state={audit}>{(events) => <AuditTable events={events} showEntity={false} />}</LoadState>
      </section>

      {rejecting && (
        <ReasonDialog
          title={rejecting === "in" ? "Reject clock-in" : "Reject clock-out"}
          intro={
            <p>
              {rejecting === "in"
                ? "The whole visit won't count towards their hours."
                : "The visit will be marked as missing a clock-out until you set a time."}
            </p>
          }
          confirmLabel="Reject"
          danger
          onConfirm={async (reason) => {
            updated(
              await post<Visit>(`/admin/visits/${v.id}/reject`, { part: rejecting, reason }),
              "Rejected.",
            );
          }}
          onClose={() => setRejecting(null)}
        />
      )}
      {editing && sites.data && (
        <EditVisitDialog
          visit={v}
          sites={sites.data}
          onClose={() => setEditing(false)}
          onSaved={(nv) => {
            setEditing(false);
            updated(nv, "Changes saved.");
          }}
        />
      )}
    </div>
  );
}
